import { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiResponseType } from "@/Api";
import { ExportFormat } from "@/Api/gameBank";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MIME: Record<ExportFormat, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv;charset=utf-8;",
};

/** Mensagem de erro da API que veio dentro do Blob (o download é pedido com responseType = blob) */
const errorMessage = async (data: unknown) => {
  const fallback = "Não foi possível exportar.";
  if (!(data instanceof Blob)) return fallback;
  try {
    const json = JSON.parse(await data.text());
    return json?.message || fallback;
  } catch {
    return fallback;
  }
};

/** Nome de arquivo sem acento nem espaço: "Mega-Sena 20 dezenas" vira "mega-sena-20-dezenas" */
export const fileSlug = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Botão "Exportar" com Excel (.xlsx) e CSV (.csv). O arquivo é gerado pela API; aqui só baixa.
 * Banco de Jogos (LOT-62): usado em "Ver jogos" e na planilha.
 */
export const ExportMenu = ({
  request,
  fileName,
  disabled,
  size,
}: {
  /** Chamada da API que devolve o arquivo (responseType = blob) */
  request: (format: ExportFormat) => apiResponseType;
  /** Nome do arquivo sem extensão */
  fileName: string;
  disabled?: boolean;
  size?: "default" | "sm";
}) => {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const response = await request(format);

      // Em caso de erro o interceptor devolve `data: {}`; com o erro em JSON, ele vem dentro do Blob
      if (!response?.ok || !(response.data instanceof Blob) || response.data.type?.includes("application/json")) {
        throw new Error(await errorMessage(response?.data));
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: MIME[format] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Arquivo ${format === "xlsx" ? "Excel" : "CSV"} baixado.`);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : "Não foi possível exportar.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size} disabled={disabled || exporting !== null} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport("xlsx")} className="cursor-pointer gap-2">
          <FileSpreadsheet className="h-4 w-4" />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("csv")} className="cursor-pointer gap-2">
          <FileText className="h-4 w-4" />
          CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
